const makeSection = (id, title, description, anchorId, bullets = [], tips = []) => ({
  id,
  title,
  description,
  anchorId,
  bullets,
  tips,
});

const makeTroubleshooting = (items) => items;

export const REPORT_HELP = {
  LIVE_DASHBOARD: {
    title: "Help Guide: Live Dashboard",
    subtitle: "A quick snapshot of performance for any date range.",
    sections: [
      makeSection(
        "overview",
        "Overview",
        "Get a fast KPI snapshot for the selected date range.",
        "live-dashboard-overview",
        [
          "Run the report to refresh KPIs.",
          "Use this view for daily check-ins before opening or after closing.",
        ],
        ["Compare the same weekday to reduce noise."]
      ),
      makeSection(
        "filters",
        "Filters",
        "Set the date range, bucket, and rebook window.",
        "live-dashboard-filters",
        [
          "Start and end dates control the reporting window.",
          "Bucket changes how the trend chart groups results.",
        ],
        ["Shorter ranges make trend shifts easier to spot."]
      ),
      makeSection(
        "kpis",
        "KPI Snapshot",
        "Review sales, average ticket, revenue per booked minute, rebooking, and utilization.",
        "live-dashboard-kpis",
        [
          "Scan KPI cards for quick performance signals.",
          "Revenue per booked minute shows how efficiently booked time is turning into sales.",
          "Use utilization to guide staffing decisions.",
        ],
        []
      ),
      makeSection(
        "details",
        "Details",
        "Revenue trend and top services for the range.",
        "live-dashboard-details",
        ["Use the trend to spot spikes or dips.", "Top services show what is driving tickets."],
        []
      ),
      makeSection(
        "export",
        "Export",
        "No export is available on this report.",
        "live-dashboard-export",
        ["Use other reports if you need CSV or PDF exports."],
        []
      ),
    ],
    troubleshooting: makeTroubleshooting([
      {
        title: "No data showing",
        body: "Check the date range and confirm tickets exist for the period.",
      },
      {
        title: "Report will not run",
        body: "Fix the date range error and try again.",
      },
    ]),
  },
  TOTAL_SHEET: {
    title: "Help Guide: Total Sheet",
    subtitle: "Summary of sales, tips, refunds, taxes, and payment totals.",
    sections: [
      makeSection(
        "overview",
        "Overview",
        "A full sales summary for the selected date range.",
        "total-sheet-overview",
        ["Use this report for daily or weekly reconciliation."],
        ["Match against Daily Closing for accuracy."]
      ),
      makeSection(
        "filters",
        "Filters",
        "Pick the date range and quick picks.",
        "total-sheet-filters",
        ["Quick picks speed up common ranges."],
        []
      ),
      makeSection(
        "kpis",
        "Summary KPIs",
        "Topline sales, income, and payment totals.",
        "total-sheet-kpis",
        ["Review totals before diving into details."],
        []
      ),
      makeSection(
        "details",
        "Details",
        "Section breakdowns and employee totals.",
        "total-sheet-details",
        ["Use mix percentages to spot shifts."],
        []
      ),
      makeSection(
        "export",
        "Export",
        "Export detailed CSV or PDF files.",
        "total-sheet-export",
        ["Use Detail Export for raw line items.", "PDF is best for sharing summaries."],
        []
      ),
    ],
    troubleshooting: makeTroubleshooting([
      {
        title: "Summary not loading",
        body: "Try a smaller date range or refresh the page.",
      },
      {
        title: "Export failed",
        body: "Try again or switch to CSV if PDF is large.",
      },
    ]),
  },
  PROFITABILITY_ESTIMATE: {
    title: "Help Guide: Profitability Estimate",
    subtitle: "Estimate profit using your revenue and expense assumptions.",
    sections: [
      makeSection(
        "overview",
        "Overview",
        "Estimate profit and margin based on selected dates and assumptions.",
        "profitability-estimate-overview",
        ["Use this to model scenarios before making changes."],
        []
      ),
      makeSection(
        "filters",
        "Filters",
        "Choose the date range and quick pick windows.",
        "profitability-estimate-filters",
        ["Quick pick helps align with month, week, or year."],
        []
      ),
      makeSection(
        "kpis",
        "Result KPIs",
        "Revenue, expenses, profit, and margin.",
        "profitability-estimate-kpis",
        ["Run Estimate to refresh KPIs."],
        []
      ),
      makeSection(
        "details",
        "Details",
        "Expense categories and breakdown table.",
        "profitability-estimate-details",
        ["Adjust categories to model different outcomes."],
        []
      ),
      makeSection(
        "export",
        "Export",
        "No export is available on this report.",
        "profitability-estimate-export",
        ["Use screenshots or share the KPIs if needed."],
        []
      ),
    ],
    troubleshooting: makeTroubleshooting([
      {
        title: "Estimate looks off",
        body: "Review expense assumptions and payroll source settings.",
      },
      {
        title: "Unable to run estimate",
        body: "Check date range and required fields, then try again.",
      },
    ]),
  },
  SALES_BY_EMPLOYEE: {
    title: "Help Guide: Sales by Employee",
    subtitle: "Compare staff sales, tips, refunds, and payment mix.",
    sections: [
      makeSection(
        "overview",
        "Overview",
        "Compare staff sales performance for a date range.",
        "sales-by-employee-overview",
        ["Use this report for coaching and incentives."],
        []
      ),
      makeSection(
        "filters",
        "Filters",
        "Select date range and grouping options.",
        "sales-by-employee-filters",
        ["Use filters to narrow to specific staff or locations."],
        []
      ),
      makeSection(
        "kpis",
        "Summary KPIs",
        "Topline totals and key stats for the selected window.",
        "sales-by-employee-kpis",
        ["Scan KPIs before the table."],
        []
      ),
      makeSection(
        "details",
        "Details",
        "Employee table with sales, tips, and payment mix.",
        "sales-by-employee-details",
        ["Sort columns to surface outliers."],
        []
      ),
      makeSection(
        "export",
        "Export",
        "Export the current view to CSV.",
        "sales-by-employee-export",
        ["Use CSV for deeper analysis."],
        []
      ),
    ],
    troubleshooting: makeTroubleshooting([
      { title: "Staff missing", body: "Check date range and filters." },
      { title: "CSV not downloading", body: "Try again or refresh the page." },
    ]),
  },
  TICKET_SUMMARY: {
    title: "Help Guide: Ticket Summary",
    subtitle: "Summarize ticket sales by employee and payment type.",
    sections: [
      makeSection(
        "overview",
        "Overview",
        "Summarize ticket sales for a selected date range.",
        "ticket-summary-overview",
        ["Use this for quick sales reconciliation."],
        []
      ),
      makeSection(
        "filters",
        "Filters",
        "Select date range and staff filters.",
        "ticket-summary-filters",
        ["Use quick pick for common ranges."],
        []
      ),
      makeSection(
        "kpis",
        "Summary KPIs",
        "Totals for sales, tips, and payments.",
        "ticket-summary-kpis",
        ["KPIs update when you run the report."],
        []
      ),
      makeSection(
        "details",
        "Details",
        "Ticket rows and breakdowns by employee.",
        "ticket-summary-details",
        ["Sort and scan for anomalies."],
        []
      ),
      makeSection(
        "export",
        "Export",
        "Export CSV for sharing or analysis.",
        "ticket-summary-export",
        ["Use CSV for accounting review."],
        []
      ),
    ],
    troubleshooting: makeTroubleshooting([
      { title: "No rows", body: "Confirm tickets exist for the range." },
      { title: "Export failed", body: "Try again or reduce the range." },
    ]),
  },
  APPOINTMENT_STATUS: {
    title: "Help Guide: Appointment Status Report",
    subtitle: "See appointments by status, staff, and time.",
    sections: [
      makeSection(
        "overview",
        "Overview",
        "Track appointments by status for a chosen range.",
        "appointment-status-overview",
        ["Use this to spot cancellations and no-shows quickly."],
        []
      ),
      makeSection(
        "filters",
        "Filters",
        "Set date range, status, staff, and grouping.",
        "appointment-status-filters",
        ["Grouping helps reveal time-of-day patterns."],
        []
      ),
      makeSection(
        "kpis",
        "Summary",
        "Totals by status and key counts.",
        "appointment-status-kpis",
        ["Scan summary before drilling into rows."],
        []
      ),
      makeSection(
        "details",
        "Details",
        "Appointment rows and grouped breakdowns.",
        "appointment-status-details",
        ["Click a row to jump to the schedule if available."],
        []
      ),
      makeSection(
        "export",
        "Export",
        "No export is available on this report.",
        "appointment-status-export",
        ["Use other reports for CSV exports if needed."],
        []
      ),
    ],
    troubleshooting: makeTroubleshooting([
      { title: "Missing appointments", body: "Check date range and filters." },
      { title: "Grouping looks off", body: "Clear filters and rerun." },
    ]),
  },
  CANCELLATION_ANALYTICS: {
    title: "Help Guide: Cancellations & No-Shows",
    subtitle: "Track cancellations, reschedules, and late change attempts.",
    sections: [
      makeSection(
        "overview",
        "Overview",
        "Monitor cancellations and no-shows for the selected range.",
        "cancellations-overview",
        ["Use this report to identify patterns quickly."],
        []
      ),
      makeSection(
        "filters",
        "Filters",
        "Set date range, staff, and grouping options.",
        "cancellations-filters",
        ["Use filters to focus on problem areas."],
        []
      ),
      makeSection(
        "kpis",
        "Summary KPIs",
        "Totals for cancellations, no-shows, and reschedules.",
        "cancellations-kpis",
        ["Look for spikes by day or time."],
        []
      ),
      makeSection(
        "details",
        "Details",
        "Breakdowns and top reasons.",
        "cancellations-details",
        ["Use charts to spot patterns."],
        []
      ),
      makeSection(
        "export",
        "Export",
        "Export a data file for sharing or analysis.",
        "cancellations-export",
        ["Use export for deeper analysis."],
        []
      ),
    ],
    troubleshooting: makeTroubleshooting([
      { title: "No cancellations", body: "Check date range and staff filters." },
      { title: "Export failed", body: "Try again or shorten the range." },
    ]),
  },
  MARKETING_CAMPAIGNS: {
    title: "Help Guide: Marketing Campaign Performance",
    subtitle: "Track campaign results, conversions, and revenue impact.",
    sections: [
      makeSection(
        "overview",
        "Overview",
        "Review campaign performance by date range.",
        "marketing-campaigns-overview",
        ["Use this report to compare campaign outcomes."],
        []
      ),
      makeSection(
        "filters",
        "Filters",
        "Set date range, goal, and status filters.",
        "marketing-campaigns-filters",
        ["Quick picks help with standard windows."],
        []
      ),
      makeSection(
        "kpis",
        "Summary KPIs",
        "Totals for campaigns, bookings, and revenue.",
        "marketing-campaigns-kpis",
        ["Use KPIs for a fast read."],
        []
      ),
      makeSection(
        "details",
        "Details",
        "Campaign table with opens, clicks, and revenue.",
        "marketing-campaigns-details",
        ["Open a campaign to view details."],
        []
      ),
      makeSection(
        "export",
        "Export",
        "No export is available on this report.",
        "marketing-campaigns-export",
        ["Use the table for quick review."],
        []
      ),
    ],
    troubleshooting: makeTroubleshooting([
      { title: "No campaigns", body: "Check date range and filters." },
      { title: "Numbers look low", body: "Confirm campaign status and dates." },
    ]),
  },
  EMAIL_REMINDERS: {
    title: "Help Guide: Email Reminders",
    subtitle: "Set up and manage your nail refill reminder email template.",
    sections: [
      makeSection(
        "overview",
        "Overview",
        "Email reminders automatically send to clients who are subscribed to Email Marketing and enabled for refill reminders.",
        "overview",
        [],
        ["Keep subject lines short for better open rates."]
      ),
      makeSection(
        "template-setup",
        "Template Setup",
        "Edit the default template or create your own.",
        "template-setup",
        ["Set one template as Active to be used for reminders."],
        []
      ),
      makeSection(
        "variables",
        "Variables",
        "Use merge fields so each email is personalized.",
        "variables",
        [
          "{first_name}",
          "{salon_name}",
          "{salon_phone}",
          "{salon_address}",
          "{booking_url}",
        ],
        ["If booking_url is blank, include a phone number in the message."]
      ),
      makeSection(
        "saving-using-template",
        "Saving & Using the Template",
        "Save your template changes to control future reminder sends.",
        "template-setup",
        ["Use an Active template for live reminder delivery."],
        []
      ),
      makeSection(
        "client-settings",
        "Client Settings",
        "Reminder eligibility depends on profile and subscription status.",
        "client-settings",
        [
          "Reminders only send to clients who are subscribed to Email Marketing.",
          "Refill reminders appear on client profiles after a service containing 'full set' or 'refill'.",
          "Choose interval 2-5 weeks.",
        ],
        []
      ),
      makeSection(
        "troubleshooting",
        "Troubleshooting",
        "Quick checks for common reminder issues.",
        "troubleshooting",
        [
          "No emails sending: confirm client is subscribed + has email + reminder enabled.",
          "Template not found: activate a template.",
          "Booking link missing: add booking URL or include phone number.",
          "Automation: run POST /system/jobs/run-refill-reminder-email-queue on a cron schedule.",
        ],
        []
      ),
    ],
    troubleshooting: makeTroubleshooting([
      {
        title: "No emails sending",
        body: "Confirm client is subscribed to Email Marketing, has an email, and has refill reminders enabled.",
      },
      {
        title: "Template not found",
        body: "Set at least one refill reminder template to Active.",
      },
      {
        title: "Booking link missing",
        body: "Add a booking URL in the template or include a phone number fallback.",
      },
      {
        title: "Automation",
        body: "Set up a cron call to POST /system/jobs/run-refill-reminder-email-queue (for example every 15 minutes).",
      },
    ]),
  },
  EMAIL_SUBSCRIPTION: {
    title: "Help Guide: Email Subscription Report",
    subtitle: "Monitor subscriber growth, unsubscribe reasons, and invite batches.",
    sections: [
      makeSection(
        "overview",
        "Overview",
        "Monitor subscriber health over time.",
        "email-subscription-overview",
        ["Use this report for opt-in performance."],
        []
      ),
      makeSection(
        "filters",
        "Filters",
        "Set date range and segment filters.",
        "email-subscription-filters",
        ["Segment filters focus on specific lists."],
        []
      ),
      makeSection(
        "kpis",
        "Summary KPIs",
        "Subscriber counts, unsubscribes, and trends.",
        "email-subscription-kpis",
        ["Scan KPIs before trends."],
        []
      ),
      makeSection(
        "details",
        "Details",
        "Trends, unsubscribe reasons, and invite batches.",
        "email-subscription-details",
        ["Use reasons to improve messaging."],
        []
      ),
      makeSection(
        "export",
        "Export",
        "No export is available on this report.",
        "email-subscription-export",
        ["Use screenshots for quick sharing."],
        []
      ),
    ],
    troubleshooting: makeTroubleshooting([
      { title: "No data", body: "Check date range and segment selection." },
      { title: "Report stuck", body: "Refresh the page and try again." },
    ]),
  },
  SERVICE_ANALYTICS: {
    title: "Help Guide: Service Analytics",
    subtitle: "Understand which services drive revenue, demand, and time usage.",
    sections: [
      makeSection(
        "overview",
        "Overview",
        "Track revenue and demand by service.",
        "service-analytics-overview",
        ["Use this report to refine your menu."],
        []
      ),
      makeSection(
        "filters",
        "Filters",
        "Select date range and grouping.",
        "service-analytics-filters",
        ["Use grouping to compare trends."],
        []
      ),
      makeSection(
        "kpis",
        "Summary KPIs",
        "Top services, totals, and averages.",
        "service-analytics-kpis",
        ["Scan KPIs before charts and tables."],
        []
      ),
      makeSection(
        "details",
        "Details",
        "Service charts and tables.",
        "service-analytics-details",
        ["Use averages to identify long-duration services."],
        []
      ),
      makeSection(
        "export",
        "Export",
        "No export is available on this report.",
        "service-analytics-export",
        ["Use screenshots for quick sharing."],
        []
      ),
    ],
    troubleshooting: makeTroubleshooting([
      { title: "Missing services", body: "Check date range and service filters." },
      { title: "Charts empty", body: "Run the report again." },
    ]),
  },
  CATEGORY_ANALYTICS: {
    title: "Help Guide: Category Analytics",
    subtitle: "Track revenue mix by service category and trends.",
    sections: [
      makeSection(
        "overview",
        "Overview",
        "Track category revenue and mix.",
        "category-analytics-overview",
        ["Use category mix to guide staffing and inventory."],
        []
      ),
      makeSection(
        "filters",
        "Filters",
        "Select date range and grouping.",
        "category-analytics-filters",
        ["Adjust grouping to compare trends."],
        []
      ),
      makeSection(
        "kpis",
        "Summary KPIs",
        "Category totals and mix.",
        "category-analytics-kpis",
        ["Scan KPIs before charts."],
        []
      ),
      makeSection(
        "details",
        "Details",
        "Charts and tables by category.",
        "category-analytics-details",
        ["Use category rows to spot shifts."],
        []
      ),
      makeSection(
        "export",
        "Export",
        "No export is available on this report.",
        "category-analytics-export",
        ["Use screenshots for quick sharing."],
        []
      ),
    ],
    troubleshooting: makeTroubleshooting([
      { title: "No categories", body: "Check date range and data availability." },
      { title: "Totals look off", body: "Confirm service categories are assigned." },
    ]),
  },
  CLIENT_ANALYTICS: {
    title: "Help Guide: Client Analytics",
    subtitle: "Track new vs returning clients, retention, and rebooking.",
    sections: [
      makeSection(
        "overview",
        "Overview",
        "Understand client growth and retention.",
        "client-analytics-overview",
        ["Use this report to plan client outreach."],
        []
      ),
      makeSection(
        "filters",
        "Filters",
        "Select date range and segment filters.",
        "client-analytics-filters",
        ["Shorter ranges highlight recent changes."],
        []
      ),
      makeSection(
        "kpis",
        "Summary KPIs",
        "New vs returning, retention, and rebooking.",
        "client-analytics-kpis",
        ["Scan KPIs before charts."],
        []
      ),
      makeSection(
        "details",
        "Details",
        "Client segments, trends, and tables.",
        "client-analytics-details",
        ["Use segments to plan targeted outreach."],
        []
      ),
      makeSection(
        "export",
        "Export",
        "No export is available on this report.",
        "client-analytics-export",
        ["Use screenshots for quick sharing."],
        []
      ),
    ],
    troubleshooting: makeTroubleshooting([
      { title: "No data", body: "Check date range and data availability." },
      { title: "Segments missing", body: "Confirm client data is up to date." },
    ]),
  },
  CLIENT_SATISFACTION: {
    title: "Help Guide: Client Satisfaction",
    subtitle: "Review ratings, trends, and follow-up signals.",
    sections: [
      makeSection(
        "overview",
        "Overview",
        "Track satisfaction ratings and trends.",
        "client-satisfaction-overview",
        ["Use this report to guide coaching."],
        []
      ),
      makeSection(
        "filters",
        "Filters",
        "Select date range and trend bucket.",
        "client-satisfaction-filters",
        ["Bucket controls trend grouping."],
        []
      ),
      makeSection(
        "kpis",
        "Summary KPIs",
        "Ratings, response rate, and trend indicators.",
        "client-satisfaction-kpis",
        ["Look for low-rating spikes."],
        []
      ),
      makeSection(
        "details",
        "Details",
        "Staff breakdowns and feedback tables.",
        "client-satisfaction-details",
        ["Use staff tables to spot coaching needs."],
        []
      ),
      makeSection(
        "export",
        "Export",
        "Export CSV for staff or feedback tables.",
        "client-satisfaction-export",
        ["Use CSV for deeper analysis."],
        []
      ),
    ],
    troubleshooting: makeTroubleshooting([
      { title: "No responses", body: "Check date range and survey setup." },
      { title: "Export failed", body: "Try again or reduce the range." },
    ]),
  },
  COMPLAINT_THEMES: {
    title: "Help Guide: Complaint Themes",
    subtitle: "Track feedback themes by category, staff, and time.",
    sections: [
      makeSection(
        "overview",
        "Overview",
        "Analyze complaint themes and counts.",
        "complaint-themes-overview",
        ["Use this report to prioritize fixes."],
        []
      ),
      makeSection(
        "filters",
        "Filters",
        "Select date range and bucket.",
        "complaint-themes-filters",
        ["Use buckets to compare time periods."],
        []
      ),
      makeSection(
        "kpis",
        "Summary KPIs",
        "Top themes and counts.",
        "complaint-themes-kpis",
        ["Review top themes before details."],
        []
      ),
      makeSection(
        "details",
        "Details",
        "Tables and breakdowns by channel and staff.",
        "complaint-themes-details",
        ["Use breakdowns to assign follow-up actions."],
        []
      ),
      makeSection(
        "export",
        "Export",
        "Export CSV for deeper analysis.",
        "complaint-themes-export",
        ["Use CSV to share with managers."],
        []
      ),
    ],
    troubleshooting: makeTroubleshooting([
      { title: "No themes", body: "Check date range or feedback sources." },
      { title: "Export failed", body: "Try again or shorten the range." },
    ]),
  },
  GIFT_CARD_ANALYTICS: {
    title: "Help Guide: Gift Card Analytics",
    subtitle: "Track gift card sales, redemptions, balances, and liability.",
    sections: [
      makeSection(
        "overview",
        "Overview",
        "Monitor gift card activity and liability.",
        "gift-card-analytics-overview",
        ["Use this report for liability tracking."],
        []
      ),
      makeSection(
        "filters",
        "Filters",
        "Select date range and filter options.",
        "gift-card-analytics-filters",
        ["Use filters to isolate sales or redemptions."],
        []
      ),
      makeSection(
        "kpis",
        "Summary KPIs",
        "Totals for sold, redeemed, and balances.",
        "gift-card-analytics-kpis",
        ["Scan KPIs before detail tables."],
        []
      ),
      makeSection(
        "details",
        "Details",
        "Tables for gift card activity and balances.",
        "gift-card-analytics-details",
        ["Sort tables to find large balances."],
        []
      ),
      makeSection(
        "export",
        "Export",
        "Export CSV for balance and activity data.",
        "gift-card-analytics-export",
        ["Use CSV for reconciliation."],
        []
      ),
    ],
    troubleshooting: makeTroubleshooting([
      { title: "No gift cards", body: "Check date range and filters." },
      { title: "Export failed", body: "Try again or reduce the range." },
    ]),
  },
  REWARDS: {
    title: "Help Guide: Rewards",
    subtitle: "Monitor points earned, redeemed, unused balances, and referrals.",
    sections: [
      makeSection(
        "overview",
        "Overview",
        "Review rewards activity and liability.",
        "rewards-overview",
        ["Use this report to monitor program health."],
        []
      ),
      makeSection(
        "filters",
        "Filters",
        "Select date range for rewards activity.",
        "rewards-filters",
        ["Shorter ranges highlight recent changes."],
        []
      ),
      makeSection(
        "kpis",
        "Summary KPIs",
        "Points earned, redeemed, and liability.",
        "rewards-kpis",
        ["Use KPIs to gauge program usage."],
        []
      ),
      makeSection(
        "details",
        "Details",
        "Top unused and top redeemed clients.",
        "rewards-details",
        ["Use top lists for outreach or promotions."],
        []
      ),
      makeSection(
        "export",
        "Export",
        "No export is available on this report.",
        "rewards-export",
        ["Use screenshots for quick sharing."],
        []
      ),
    ],
    troubleshooting: makeTroubleshooting([
      { title: "No rewards data", body: "Check date range and rewards setup." },
      { title: "Totals look wrong", body: "Refresh the report and try again." },
    ]),
  },
  DAILY_CLOSING: {
    title: "Help Guide: Daily Closing Cash Report",
    subtitle: "Reconcile daily sales, cash, tips, gift cards, and payouts.",
    sections: [
      makeSection(
        "overview",
        "Overview",
        "Close out the day with cash and payout reconciliation.",
        "daily-closing-overview",
        ["Use this report at end of day."],
        []
      ),
      makeSection(
        "filters",
        "Filters",
        "Choose the work date and load tickets.",
        "daily-closing-filters",
        ["Load from tickets to prefill totals."],
        []
      ),
      makeSection(
        "kpis",
        "Summary KPIs",
        "Key totals and reconciliation status.",
        "daily-closing-kpis",
        ["Review differences before saving."],
        []
      ),
      makeSection(
        "details",
        "Details",
        "Cash, tips, gift cards, and expense sections.",
        "daily-closing-details",
        ["Use notes for any variances."],
        []
      ),
      makeSection(
        "export",
        "Export",
        "No export is available on this report.",
        "daily-closing-export",
        ["Use screenshots for sharing if needed."],
        []
      ),
    ],
    troubleshooting: makeTroubleshooting([
      { title: "Totals do not match", body: "Reload tickets and recheck entries." },
      { title: "Unable to save", body: "Verify required fields and try again." },
    ]),
  },
  CASH_BALANCE: {
    title: "Help Guide: Cash Balance Report",
    subtitle: "Track expected cash based on cash in, cash out, and closings.",
    sections: [
      makeSection(
        "overview",
        "Overview",
        "Track expected cash and compare to actuals.",
        "cash-balance-overview",
        ["Use this report for cash control."],
        []
      ),
      makeSection(
        "filters",
        "Filters",
        "Select date range and options.",
        "cash-balance-filters",
        ["Use ranges that align with closing periods."],
        []
      ),
      makeSection(
        "kpis",
        "Summary KPIs",
        "Cash in, cash out, and balance totals.",
        "cash-balance-kpis",
        ["Scan KPIs before reviewing table rows."],
        []
      ),
      makeSection(
        "details",
        "Details",
        "Daily rows and balance breakdown.",
        "cash-balance-details",
        ["Use Daily Closing for deeper reconciliation."],
        []
      ),
      makeSection(
        "export",
        "Export",
        "Export CSV for cash balance data.",
        "cash-balance-export",
        ["Use CSV for accounting review."],
        []
      ),
    ],
    troubleshooting: makeTroubleshooting([
      { title: "Missing dates", body: "Check date range and salon timezone." },
      { title: "Export failed", body: "Try again or shorten the range." },
    ]),
  },
  TAX_LIABILITY: {
    title: "Help Guide: Tax Liability Summary",
    subtitle: "Review taxable vs non-taxable sales and estimated tax owed.",
    sections: [
      makeSection(
        "overview",
        "Overview",
        "Summarize taxable and non-taxable sales.",
        "tax-liability-overview",
        ["Use this report for tax planning."],
        []
      ),
      makeSection(
        "filters",
        "Filters",
        "Select date range and staff options.",
        "tax-liability-filters",
        ["Use consistent ranges for comparisons."],
        []
      ),
      makeSection(
        "kpis",
        "Summary KPIs",
        "Taxable totals and estimated tax.",
        "tax-liability-kpis",
        ["Review summaries before exports."],
        []
      ),
      makeSection(
        "details",
        "Details",
        "Breakdowns by staff and payment type.",
        "tax-liability-details",
        ["Use details to reconcile filing."],
        []
      ),
      makeSection(
        "export",
        "Export",
        "Export summary or detail as CSV or PDF.",
        "tax-liability-export",
        ["Use summary for filing and detail for audit."],
        []
      ),
    ],
    troubleshooting: makeTroubleshooting([
      { title: "Totals look off", body: "Verify tax settings and date range." },
      { title: "Export failed", body: "Try again or switch formats." },
    ]),
  },
  SALES_TICKETS: {
    title: "Help Guide: Sales & Tickets",
    subtitle: "Review ticket volume, cancellations, sources, and peak times.",
    sections: [
      makeSection(
        "overview",
        "Overview",
        "Track ticket volume and sales trends.",
        "sales-tickets-overview",
        ["Use this report to plan staffing."],
        []
      ),
      makeSection(
        "filters",
        "Filters",
        "Select date range and comparison options.",
        "sales-tickets-filters",
        ["Use comparison to spot trends."],
        []
      ),
      makeSection(
        "kpis",
        "Summary KPIs",
        "Tickets, cancellations, and conversion signals.",
        "sales-tickets-kpis",
        ["Scan KPIs before charts."],
        []
      ),
      makeSection(
        "details",
        "Details",
        "Charts and tables for sales and ticket trends.",
        "sales-tickets-details",
        ["Use peak times to guide staffing."],
        []
      ),
      makeSection(
        "export",
        "Export",
        "Export a ZIP data file.",
        "sales-tickets-export",
        ["Use the ZIP for deep analysis."],
        []
      ),
    ],
    troubleshooting: makeTroubleshooting([
      { title: "Trends missing", body: "Check date range and run report again." },
      { title: "Export failed", body: "Try again or shorten the range." },
    ]),
  },
  STAFF_PERFORMANCE: {
    title: "Help Guide: Staff Performance",
    subtitle: "Compare utilization, sales per hour, tips, refunds, and rebooking impact.",
    sections: [
      makeSection(
        "overview",
        "Overview",
        "Compare performance metrics by staff.",
        "staff-performance-overview",
        ["Use this report for coaching and goals."],
        []
      ),
      makeSection(
        "filters",
        "Filters",
        "Select date range and staff filters.",
        "staff-performance-filters",
        ["Use filters to focus on specific staff."],
        []
      ),
      makeSection(
        "kpis",
        "Summary KPIs",
        "Key metrics for the selected range.",
        "staff-performance-kpis",
        ["Review KPIs before tables."],
        []
      ),
      makeSection(
        "details",
        "Details",
        "Staff table with utilization and sales per hour.",
        "staff-performance-details",
        ["Sort columns to spot outliers."],
        []
      ),
      makeSection(
        "export",
        "Export",
        "Export CSV for staff performance.",
        "staff-performance-export",
        ["Use CSV for further analysis."],
        []
      ),
    ],
    troubleshooting: makeTroubleshooting([
      { title: "Staff missing", body: "Check date range and filters." },
      { title: "Export failed", body: "Try again or reduce the range." },
    ]),
  },
  TRAINING_PROGRESS: {
    title: "Help Guide: Training Progress",
    subtitle: "Track assignment completion, overdue items, and progress by role or staff.",
    sections: [
      makeSection(
        "overview",
        "Overview",
        "Track training completion and overdue items.",
        "training-progress-overview",
        ["Use this report for coaching and onboarding."],
        []
      ),
      makeSection(
        "filters",
        "Filters",
        "Select date range, role, and status filters.",
        "training-progress-filters",
        ["Filter to focus on overdue items."],
        []
      ),
      makeSection(
        "kpis",
        "Summary KPIs",
        "Counts for completed, in progress, and overdue.",
        "training-progress-kpis",
        ["Scan KPIs before reviewing rows."],
        []
      ),
      makeSection(
        "details",
        "Details",
        "Training rows with module progress.",
        "training-progress-details",
        ["Expand rows for module details."],
        []
      ),
      makeSection(
        "export",
        "Export",
        "Export CSV for training progress.",
        "training-progress-export",
        ["Use CSV to share with managers."],
        []
      ),
    ],
    troubleshooting: makeTroubleshooting([
      { title: "No assignments", body: "Check date range and filters." },
      { title: "Export failed", body: "Try again or reduce the range." },
    ]),
  },
  MAINTENANCE_CLEANING_ANALYTICS: {
    title: "Help Guide: Maintenance & Cleaning Analytics",
    subtitle:
      "Track expected vs completed maintenance routines, overdue hotspots, and staff consistency.",
    sections: [
      makeSection(
        "overview",
        "Overview",
        "Shows maintenance compliance performance for your selected date range.",
        "maintenance-analytics-overview",
        [
          "Use KPI cards to monitor completion, overdue pressure, and consistency.",
          "Use task/staff tables to identify where coaching or reassignment is needed.",
        ],
        []
      ),
      makeSection(
        "filters",
        "Filters",
        "Filter by date range, assigned staff, category, frequency, status, and task.",
        "maintenance-analytics-filters",
        [
          "Date boundaries and grouping use salon timezone.",
          "All dates/times use salon timezone.",
          "Status filter applies to computed occurrence outcomes.",
          "Include inactive adds archived tasks to the analysis.",
        ],
        []
      ),
      makeSection(
        "definitions",
        "Definitions",
        "Core terms used across cards, charts, and tables.",
        "maintenance-analytics-definitions",
        [
          "Expected occurrence: a due instance generated from the task frequency rule.",
          "On-time: completion timestamp is at or before due timestamp.",
          "Late: completion timestamp is after due timestamp.",
          "Missed: expected occurrence had no completion in the reporting window.",
          "Completion rate: completed expected occurrences / expected occurrences.",
          "Consistency score: weighted on-time performance on a 0-100 scale.",
        ],
        [
          "BIWEEKLY expected occurrences are anchored to task creation date.",
          "Event-based frequencies can be excluded from expected/missed totals.",
        ]
      ),
      makeSection(
        "insights",
        "How To Use Insights",
        "Turn report outcomes into operational actions.",
        "maintenance-analytics-insights",
        [
          "Identify overdue hotspots by task/frequency to prioritize cleanup.",
          "Use staff trends for coaching on routine completion habits.",
          "Adjust task assignment or recurrence when misses stay high.",
          "Confirm compliance routines are consistent over time.",
        ],
        []
      ),
      makeSection(
        "audit",
        "Audit & Exceptions",
        "Review completion-level logs with computed status at completion time.",
        "maintenance-analytics-logs",
        ["Export CSV for compliance reviews or manager follow-ups."],
        []
      ),
    ],
  },
  GIFT_CARDS_CASH_DRAWER: {
    title: "Help Guide: Cash Drawer Analysis",
    subtitle: "Reconcile gift card activity with cash drawer over/short.",
    sections: [
      makeSection(
        "overview",
        "Overview",
        "Combine gift card liability with drawer activity.",
        "gift-cards-cash-drawer-overview",
        ["Use this report for weekly reconciliation."],
        []
      ),
      makeSection(
        "filters",
        "Filters",
        "Select date range and optional filters.",
        "gift-cards-cash-drawer-filters",
        ["Use shorter ranges for easier audits."],
        []
      ),
      makeSection(
        "kpis",
        "Summary KPIs",
        "Over/short and gift card totals.",
        "gift-cards-cash-drawer-kpis",
        ["Review totals before detail tables."],
        []
      ),
      makeSection(
        "details",
        "Details",
        "Drawer activity and gift card tables.",
        "gift-cards-cash-drawer-details",
        ["Use details to track paid-in/out activity."],
        []
      ),
      makeSection(
        "export",
        "Export",
        "Export a ZIP data file.",
        "gift-cards-cash-drawer-export",
        ["Use ZIP for detailed review."],
        []
      ),
    ],
    troubleshooting: makeTroubleshooting([
      { title: "No drawer activity", body: "Check date range and filters." },
      { title: "Export failed", body: "Try again or shorten the range." },
    ]),
  },
};
